import { FileText, Upload } from "lucide-react";
import { useEffect, useMemo, type FC } from "react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { TableRow } from "@/components/ui/table";
import { Tag } from "@/components/ui/tag";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";

import { VisaTag } from "../../../components/common/VisaTag";
import { asVisaValue } from "../../../utils/matterDisplay";
import {
  ClientTable,
  TABLE_TEXT,
  type ClientTableColumn
} from "../../clientProfile/components/table/ClientTable";
import { ClientTableRow } from "../../clientProfile/components/table/ClientTableRow";
import { ClientTableCell } from "../../clientProfile/components/table/cells";
import { useUserProfiles } from "../../userProfiles/context/UserProfilesContext";
import {
  descriptionText,
  documentFocusDomId,
  documentLabel,
  formatCreatedAt,
  referencedCaseIds,
  uploaderDisplay
} from "../documentsModel";
import type { ForeignNationalDocument } from "../types";

type Props = {
  documents: ForeignNationalDocument[];
  isLoading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  isFiltered?: boolean;
  onUpload?: () => void;
  emptyMessage?: string;
  selectedIds: Set<string>;
  onToggleSelected: (docId: string, selected: boolean) => void;
  onToggleSelectAll: (selected: boolean) => void;
  allVisibleSelected: boolean;
  onPreview: (doc: ForeignNationalDocument) => void;
  caseLabelById: Record<string, string>;
  caseVisaById: Record<string, string>;
  showReferencedInColumn?: boolean;
  showSelection?: boolean;
  /** Deep-link focus: the row for this document is highlighted. */
  highlightedDocumentId?: string | null;
  /** Firm view: the "Client sees" column. The client's own uploads are always visible. */
  sharing?: {
    clientId: string;
    onToggle: (doc: ForeignNationalDocument, shared: boolean) => void;
  };
};

const SkeletonRow: FC<{ selection: boolean; referenced: boolean }> = ({
  selection,
  referenced
}) => (
  <TableRow className="hover:bg-transparent">
    {selection ? (
      <ClientTableCell className="w-10 justify-center px-2">
        <Skeleton className="h-3 w-4" />
      </ClientTableCell>
    ) : null}
    <ClientTableCell>
      <Skeleton className="h-3 w-48" />
    </ClientTableCell>
    <ClientTableCell>
      <Skeleton className="h-3 w-72" />
    </ClientTableCell>
    <ClientTableCell className="gap-2">
      <Skeleton className="size-5 rounded-xs" />
      <Skeleton className="h-3 w-28" />
    </ClientTableCell>
    {referenced ? (
      <ClientTableCell>
        <Skeleton className="h-3 w-16" />
      </ClientTableCell>
    ) : null}
  </TableRow>
);

/** Documents list shared by the firm profile and the client portal. */
export function ForeignNationalDocumentsTable({
  documents,
  isLoading = false,
  error,
  onRetry,
  isFiltered = false,
  onUpload,
  emptyMessage = "Upload a file to start this client's document library.",
  selectedIds,
  onToggleSelected,
  onToggleSelectAll,
  allVisibleSelected,
  onPreview,
  caseLabelById,
  caseVisaById,
  showReferencedInColumn = true,
  showSelection = true,
  highlightedDocumentId = null,
  sharing
}: Props) {
  const { get, ensure } = useUserProfiles();

  const createdByUserIds = useMemo(
    () =>
      documents.map((d) => (d.created_by_user_id ?? "").trim()).filter(Boolean),
    [documents]
  );
  useEffect(() => {
    if (createdByUserIds.length === 0) return;
    ensure(createdByUserIds).catch(() => undefined);
  }, [createdByUserIds, ensure]);

  const columns = useMemo<ClientTableColumn[]>(() => {
    const cols: ClientTableColumn[] = [];
    if (showSelection) {
      cols.push({
        id: "select",
        className: "w-10 justify-center px-2",
        header: (
          <Checkbox
            aria-label="Select all documents"
            checked={allVisibleSelected}
            disabled={documents.length === 0}
            onCheckedChange={(c) => onToggleSelectAll(c === true)}
          />
        )
      });
    }
    cols.push(
      {
        id: "file",
        className: "min-w-60",
        header: "File"
      },
      { id: "description", header: "Description", className: "min-w-90" },
      { id: "uploaded", header: "Uploaded At", className: "min-w-48" }
    );
    if (showReferencedInColumn) {
      cols.push({
        id: "referenced",
        header: "Referenced In",
        className: "min-w-52"
      });
    }
    if (sharing) {
      cols.push({ id: "shared", header: "Client Sees", className: "w-32" });
    }
    return cols;
  }, [
    allVisibleSelected,
    documents.length,
    onToggleSelectAll,
    sharing,
    showReferencedInColumn,
    showSelection
  ]);

  return (
    <ClientTable
      ariaLabel="Documents"
      columns={columns}
      isLoading={isLoading}
      isEmpty={documents.length === 0}
      error={error}
      onRetry={onRetry}
      renderSkeletonRow={(i) => (
        <SkeletonRow
          key={i}
          selection={showSelection}
          referenced={showReferencedInColumn}
        />
      )}
      empty={
        isFiltered
          ? {
              title: "No Matching Documents",
              icon: FileText,
              message: "Try a different search."
            }
          : {
              title: "No Documents Yet",
              icon: FileText,
              message: emptyMessage,
              cta: onUpload ? "Upload" : undefined,
              ctaIcon: Upload,
              onCta: onUpload
            }
      }
    >
      {documents.map((doc) => {
        const createdById = (doc.created_by_user_id ?? "").trim();
        const uploader = uploaderDisplay(
          doc,
          createdById ? get(createdById) ?? null : null
        );
        const isSelected =
          showSelection && selectedIds.has(doc.foreign_national_document_id);
        const refIds = showReferencedInColumn ? referencedCaseIds(doc) : [];
        const visibleRefs = refIds.slice(0, 3);
        const overflow = Math.max(0, refIds.length - visibleRefs.length);
        const label = documentLabel(doc);

        return (
          <ClientTableRow
            key={doc.foreign_national_document_id}
            domId={documentFocusDomId(doc.foreign_national_document_id)}
            highlighted={
              highlightedDocumentId === doc.foreign_national_document_id
            }
            onActivate={() => onPreview(doc)}
            className={isSelected ? "bg-fill-2" : undefined}
            data-testid="document-row"
          >
            {showSelection ? (
              <ClientTableCell className="w-10 justify-center px-2">
                <Checkbox
                  aria-label={`Select ${doc.filename}`}
                  checked={isSelected}
                  onCheckedChange={(c) =>
                    onToggleSelected(
                      doc.foreign_national_document_id,
                      c === true
                    )
                  }
                />
              </ClientTableCell>
            ) : null}

            <ClientTableCell className="gap-2">
              <FileText className="size-4 shrink-0 text-fg-3" aria-hidden />
              <span className={`truncate ${TABLE_TEXT.primary}`}>
                {doc.filename}
              </span>
            </ClientTableCell>

            <ClientTableCell className="h-auto min-h-12 py-2 gap-2">
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                {label ? <Tag>{label}</Tag> : null}
                <span className={`min-w-0 wrap-anywhere ${TABLE_TEXT.muted}`}>
                  {descriptionText(doc).trim() || (label ? "" : "—")}
                </span>
              </div>
            </ClientTableCell>

            <ClientTableCell className="gap-2">
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    className="inline-flex"
                    data-row-stop
                    aria-label={uploader.tooltip}
                  >
                    <Avatar size="xs">
                      {uploader.avatarUrl ? (
                        <AvatarImage src={uploader.avatarUrl} alt="" />
                      ) : null}
                      <AvatarFallback>{uploader.initials}</AvatarFallback>
                    </Avatar>
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top" className="whitespace-pre-line">
                  {uploader.tooltip}
                </TooltipContent>
              </Tooltip>
              <span
                className={`truncate whitespace-nowrap ${TABLE_TEXT.muted}`}
              >
                {formatCreatedAt(doc.created_at)}
              </span>
            </ClientTableCell>

            {showReferencedInColumn ? (
              <ClientTableCell>
                {refIds.length === 0 ? (
                  <span className={TABLE_TEXT.muted}>—</span>
                ) : (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div
                        className="flex flex-wrap items-center gap-1.5"
                        data-row-stop
                      >
                        {visibleRefs.map((caseId) => (
                          <VisaTag
                            key={caseId}
                            visaType={asVisaValue(
                              (caseVisaById[caseId] ?? "").trim()
                            )}
                            size="xs"
                          />
                        ))}
                        {overflow > 0 ? <Tag>+{overflow}</Tag> : null}
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="whitespace-pre-line">
                      {refIds
                        .map(
                          (id) => `${(caseLabelById[id] ?? id).trim()} (${id})`
                        )
                        .join("\n")}
                    </TooltipContent>
                  </Tooltip>
                )}
              </ClientTableCell>
            ) : null}

            {sharing ? (
              <ClientTableCell className="w-32" data-row-stop>
                {createdById && createdById === sharing.clientId ? (
                  <span className={TABLE_TEXT.muted}>Their upload</span>
                ) : (
                  <Switch
                    size="sm"
                    checked={Boolean(doc.shared_with_client)}
                    onCheckedChange={(on) => sharing.onToggle(doc, on)}
                    aria-label={`Share ${doc.filename} with the client`}
                    data-testid="document-shared-toggle"
                  />
                )}
              </ClientTableCell>
            ) : null}
          </ClientTableRow>
        );
      })}
    </ClientTable>
  );
}
